import { createContext, useCallback, useContext, useState, type ReactNode } from "react";

/**
 * Sprint 12 — slide-in row-detail panel state, mounted at AppShell level so any
 * surface (a table row, an FK chip, Cmd-K) can open a record without prop
 * drilling. Mirrors Sprint 9a's RightRailContext pattern.
 *
 * The panel is a STACK so clicking a foreign-key chip inside a panel replaces it
 * with the target's detail and offers a "← Back" affordance. Depth is capped at
 * {@link MAX_STACK_DEPTH} to avoid panel-in-panel-in-panel disorientation
 * (Linear's pattern). State is transient — closing and reopening starts fresh.
 */

export const MAX_STACK_DEPTH = 3;

export interface PanelStackEntry {
  entityTypeSlug: string;
  entityId: string;
}

interface RowDetailContextValue {
  stack: PanelStackEntry[];
  /** Top-of-stack entry, or null when the panel is closed. */
  current: PanelStackEntry | null;
  open: boolean;
  /** Open the panel fresh on this record (resets any existing stack). */
  openPanel: (entityTypeSlug: string, entityId: string) => void;
  /** Replace the current panel with this record, keeping a back affordance. */
  pushPanel: (entityTypeSlug: string, entityId: string) => void;
  /** Step back one level (closes the panel at depth 1). */
  popPanel: () => void;
  /** Close the panel entirely and clear the stack. */
  closePanel: () => void;
}

const RowDetailContext = createContext<RowDetailContextValue | null>(null);

export function RowDetailProvider({ children }: { children: ReactNode }) {
  const [stack, setStack] = useState<PanelStackEntry[]>([]);

  const openPanel = useCallback((entityTypeSlug: string, entityId: string) => {
    setStack([{ entityTypeSlug, entityId }]);
  }, []);

  const pushPanel = useCallback((entityTypeSlug: string, entityId: string) => {
    setStack((prev) => {
      // Ignore a no-op push onto the same record.
      const top = prev[prev.length - 1];
      if (top && top.entityTypeSlug === entityTypeSlug && top.entityId === entityId) return prev;
      const next = [...prev, { entityTypeSlug, entityId }];
      return next.length > MAX_STACK_DEPTH ? next.slice(next.length - MAX_STACK_DEPTH) : next;
    });
  }, []);

  const popPanel = useCallback(() => {
    setStack((prev) => prev.slice(0, -1));
  }, []);

  const closePanel = useCallback(() => setStack([]), []);

  const value: RowDetailContextValue = {
    stack,
    current: stack[stack.length - 1] ?? null,
    open: stack.length > 0,
    openPanel,
    pushPanel,
    popPanel,
    closePanel,
  };

  return <RowDetailContext.Provider value={value}>{children}</RowDetailContext.Provider>;
}

export function useRowDetailPanel(): RowDetailContextValue {
  const ctx = useContext(RowDetailContext);
  if (!ctx) throw new Error("useRowDetailPanel must be used within a RowDetailProvider");
  return ctx;
}

/**
 * Provider-optional variant. Returns null when there is no RowDetailProvider in
 * the tree (e.g. the v3 App-Shell record/list surfaces, which navigate to a full
 * record page instead of opening the Sprint-12 slide-in panel). Components shared
 * across both worlds (EntityChip) use this so they render safely everywhere and
 * fall back to their `onOpen`/navigation prop rather than crashing.
 */
export function useRowDetailPanelOptional(): RowDetailContextValue | null {
  return useContext(RowDetailContext);
}
